import React, { useEffect, useState } from 'react';
import { Copy, Stack } from '@grubhubprod/gh-cookbook-react';
import { get } from '@grubhubprod/umami-library/dist/nodash/get';
import {
  useSignupStatus,
  SignupStep,
} from 'src/components/selfService/features/Signup/context/SignupStatusProvider/SignupStatusProvider';
import { ConfirmationStepCardContainer } from 'src/components/selfService/features/Signup/steps/ConfirmationStep/ConfirmationStepCardContainer/ConfirmationStepCardContainer';
import { sdk } from 'src/utilities/sdk';
import { SelfServiceCorp } from 'src/types/Corp';

interface IBusinessDetails {
  name: string;
  address: string;
  floor: string;
  phone: string;
  city: string;
  state: string;
  zip: string;
}

interface IBusinessDetailsConfirmationCardProps {
  onDataLoaded: () => void;
}

export function BusinessDetailsConfirmationCard({
  onDataLoaded,
}: IBusinessDetailsConfirmationCardProps): JSX.Element {
  const { getStepNumber, corpId } = useSignupStatus();
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [businessDetails, setBusinessDetails] =
    useState<IBusinessDetails>(null);

  async function getBusinessDetails() {
    setHasError(false);
    setIsLoadingData(true);
    try {
      const response = await sdk.http.get(`/corp-admin/corp/self/${corpId}`);

      const corp = get(response, 'object') as SelfServiceCorp;

      if (corp) {
        const {
          name,
          location: { address, floor, phone, city, state, zip },
        } = corp;

        setBusinessDetails({
          name,
          address,
          floor,
          phone,
          city,
          state,
          zip,
        });
      } else {
        setHasError(true);
      }
    } catch (error) {
      setHasError(true);
    } finally {
      setIsLoadingData(false);
    }
  }

  useEffect(() => {
    if (corpId) {
      getBusinessDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [corpId]);

  useEffect(() => {
    if (businessDetails) {
      onDataLoaded();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessDetails]);

  return (
    <ConfirmationStepCardContainer
      stepName="Business profile"
      isLoadingData={isLoadingData}
      linkText="Edit"
      stepNumber={getStepNumber(SignupStep.COMPANY)}
      reloadData={getBusinessDetails}
      hasError={hasError}
    >
      {businessDetails && (
        <>
          <Stack y="xxx-sm">
            <Copy variant="body">{businessDetails.name}</Copy>
          </Stack>
          <Stack y="xxx-sm">
            <Copy variant="body">
              {businessDetails.address} {businessDetails.floor}
            </Copy>
          </Stack>
          <Stack y="xxx-sm">
            <Copy variant="body">
              {`${businessDetails.city}, ${businessDetails.state} ${businessDetails.zip}`}
            </Copy>
          </Stack>
          <Stack y="xxx-sm">
            <Copy variant="body">{businessDetails.phone}</Copy>
          </Stack>
        </>
      )}
    </ConfirmationStepCardContainer>
  );
}
